/**
 * moonshot-split-checkout.js
 *
 * Split-checkout orchestrator. When a customer has a mixed ship+pickup
 * cart, this module:
 *   1. Detects the mix on /cart.
 *   2. Generates a splitGroupId and stashes the pickup items
 *      (localStorage for everyone; customer metafield via the
 *      inventory-proxy Worker for logged-in customers — cross-device).
 *   3. Removes pickup lines from the live cart and writes a
 *      `moonshot_split_group_id` cart attribute so order #1 carries
 *      the group identifier.
 *   4. Redirects to /checkout for the ship order.
 *   5. On the thank-you page (or any subsequent page), the resume
 *      banner script reads the pending state, restores the pickup
 *      items, re-applies the split group attribute, and bounces to
 *      /checkout for order #2.
 *
 * This file is the storage + state-machine layer. The UI surfaces
 * (cart split view, thank-you banner, site-wide recovery banner) live
 * in their own files and call into window.MoonshotSplit.
 *
 * Two orders sharing the same `moonshot_split_group_id` attribute is
 * the canonical "these were one customer purchase" signal for ops.
 */
(function () {
  if (typeof window === "undefined") return;

  const STORAGE_KEY = "moonshot_split_state";
  const WORKER_PATH = "/apps/moonshot/pending-cart";
  const SPLIT_ATTRIBUTE_KEY = "moonshot_split_group_id";

  const ns = (window.MoonshotSplit = window.MoonshotSplit || {});

  // ─────────────────────────────────────────────────────────────────────
  // Storage — localStorage is the per-device source of truth; Worker is
  // best-effort sync for logged-in customers across devices.
  // ─────────────────────────────────────────────────────────────────────
  function readLocal() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function writeLocal(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // Quota / private mode — silently fall back to in-memory only.
    }
  }

  function clearLocal() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  async function readRemote() {
    try {
      const res = await fetch(WORKER_PATH, {
        headers: { Accept: "application/json" },
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data || !data.hasPending) return null;
      return {
        splitGroupId: data.splitGroupId,
        items: data.items || [],
        stashedAt: data.stashedAt,
        pickupGroup: data.pickupGroup || null,
        stage: data.stage || "awaiting-ship",
      };
    } catch (e) {
      return null;
    }
  }

  async function writeRemote(state) {
    try {
      await fetch(WORKER_PATH, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(state),
      });
    } catch (e) {
      // Non-fatal. localStorage is authoritative on this device.
    }
  }

  async function clearRemote() {
    try {
      await fetch(WORKER_PATH, {
        method: "DELETE",
        headers: { Accept: "application/json" },
        credentials: "include",
      });
    } catch (e) {
      /* ignore */
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Cart analysis
  // ─────────────────────────────────────────────────────────────────────
  function getFulfillment(item) {
    if (!item || !item.properties) return "ship";
    return item.properties.fulfillment || item.properties._fulfillment || "ship";
  }

  function isPickupValue(v) {
    return typeof v === "string" && v.indexOf("pickup:") === 0;
  }

  function pickupGroupOf(v) {
    return isPickupValue(v) ? v.slice("pickup:".length) : null;
  }

  function analyzeCart(cart) {
    const result = {
      isMixed: false,
      hasShip: false,
      hasPickup: false,
      hasLocker: false,
      shipItems: [],
      pickupItems: [],
      lockerItems: [],
      pickupGroup: null,
      shipTotalCents: 0,
      pickupTotalCents: 0,
      lockerTotalCents: 0,
    };
    if (!cart || !Array.isArray(cart.items)) return result;
    for (const item of cart.items) {
      const f = getFulfillment(item);
      const lineTotal = (item.line_price || item.final_line_price || 0) | 0;
      if (f === "locker") {
        result.hasLocker = true;
        result.lockerItems.push(item);
        result.lockerTotalCents += lineTotal;
      } else if (isPickupValue(f)) {
        result.hasPickup = true;
        result.pickupItems.push(item);
        result.pickupGroup = pickupGroupOf(f);
        result.pickupTotalCents += lineTotal;
      } else {
        result.hasShip = true;
        result.shipItems.push(item);
        result.shipTotalCents += lineTotal;
      }
    }
    // isMixed when 2+ distinct modes are present. Covers ship+pickup
    // (legacy), ship+locker, pickup+locker, ship+pickup+locker.
    const modeCount =
      (result.hasShip ? 1 : 0) +
      (result.hasPickup ? 1 : 0) +
      (result.hasLocker ? 1 : 0);
    result.isMixed = modeCount >= 2;
    return result;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Cart operations
  // ─────────────────────────────────────────────────────────────────────
  async function getCart() {
    try {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  // Set quantity 0 on each provided item key — Shopify's idiom for "remove."
  // Sequential to keep cart-validation Function from rejecting an in-flight
  // state (escape hatch is set in moonshot-cart-validation.js before bulk
  // ops; we set it here too in case this runs outside that context).
  async function removeLines(items) {
    for (const item of items) {
      if (!item || !item.key) continue;
      try {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      } catch (e) {
        console.warn("[moonshot-split] failed to remove line", item.key, e);
      }
    }
  }

  async function setSplitGroupAttribute(splitGroupId) {
    const attrs = {};
    attrs[SPLIT_ATTRIBUTE_KEY] = splitGroupId;
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (e) {
      console.warn("[moonshot-split] failed to set cart attribute", e);
    }
  }

  // Set a short skip-validation window before bulk cart mutations so the
  // Cart Validation Function doesn't reject intermediate states.
  async function setSkipValidation() {
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: { moonshot_skip_validation_at: String(Date.now()) },
        }),
      });
    } catch (e) {
      /* ignore */
    }
  }

  async function clearSkipValidation() {
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attributes: { moonshot_skip_validation_at: "" },
        }),
      });
    } catch (e) {
      /* ignore */
    }
  }

  async function addItems(items) {
    const payload = {
      items: items.map(function (i) {
        return {
          id: i.variantId,
          quantity: i.quantity,
          properties: i.properties || {},
        };
      }),
    };
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(function () {
        return {};
      });
      throw new Error(body.description || "Add to cart failed (" + res.status + ")");
    }
    return res.json();
  }

  // ─────────────────────────────────────────────────────────────────────
  // State machine
  // ─────────────────────────────────────────────────────────────────────
  function generateSplitId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return "split-" + Math.random().toString(36).slice(2) + "-" + Date.now();
  }

  async function getPending() {
    const local = readLocal();
    if (local && local.items && local.items.length > 0) return local;
    const remote = await readRemote();
    if (remote && remote.items && remote.items.length > 0) {
      writeLocal(remote);
      return remote;
    }
    return null;
  }

  async function hasPending() {
    const p = await getPending();
    return !!p;
  }

  // Public: start a split. Reads cart, stashes pickup, writes attribute,
  // redirects to /checkout for the ship order. Returns a promise so
  // callers can show a loading state.
  async function startSplit(options) {
    const opts = options || {};
    const cart = await getCart();
    if (!cart) throw new Error("Cart unavailable");
    const analysis = analyzeCart(cart);
    if (!analysis.isMixed) {
      // Nothing to split; just go to checkout.
      window.location.href = opts.checkoutUrl || "/checkout";
      return;
    }
    const splitGroupId = generateSplitId();

    // Strategy: ship goes first if present (immediate revenue +
    // standard customer expectation). Stash everything else.
    //
    // For 3-mode carts (ship + pickup + locker): we stash both
    // pickup AND locker together. After the ship checkout completes,
    // the resume restores both, leaving the cart in a 2-mode state
    // (pickup + locker). Customer lands back on /cart, sees the
    // split UI again, and triggers a second split. Recursive 2-mode
    // is simpler than a true N-stage queue and works for any
    // combination of present modes.
    //
    // First-stage decision:
    //   has ship          → ship is current; stash everything else
    //   no ship, has pickup → pickup is current; stash locker
    //   no ship, no pickup → impossible (isMixed requires 2+ modes;
    //                       locker-only doesn't trigger this path)
    let itemsToStash = [];
    let stashPickupGroup = null;
    if (analysis.hasShip) {
      itemsToStash = analysis.pickupItems.concat(analysis.lockerItems);
      stashPickupGroup = analysis.pickupGroup; // may be null if no pickup
    } else if (analysis.hasPickup) {
      itemsToStash = analysis.lockerItems;
      stashPickupGroup = null; // pickup is the current cart; locker is stashed
    }
    if (itemsToStash.length === 0) {
      // Defensive — shouldn't reach here because isMixed should imply
      // something to stash. Just go to checkout normally.
      window.location.href = opts.checkoutUrl || "/checkout";
      return;
    }

    const pendingItems = itemsToStash.map(function (i) {
      return {
        variantId: String(i.variant_id),
        quantity: i.quantity,
        properties: i.properties || {},
      };
    });
    const state = {
      splitGroupId: splitGroupId,
      items: pendingItems,
      stashedAt: new Date().toISOString(),
      // pickupGroup preserved for recovery banner copy; null if no
      // pickup items are stashed (locker-only stash).
      pickupGroup: stashPickupGroup,
      stage: "awaiting-current",
    };
    writeLocal(state);
    // Fire-and-forget; localStorage is the immediate source of truth.
    writeRemote(state);
    await setSkipValidation();
    await removeLines(itemsToStash);
    await setSplitGroupAttribute(splitGroupId);
    await clearSkipValidation();
    window.location.href = opts.checkoutUrl || "/checkout";
  }

  // Public: resume a pending pickup order. Adds the stashed items back
  // to the cart, re-applies the split group attribute, redirects to
  // /checkout.
  async function resumePickup() {
    const state = await getPending();
    if (!state || !state.items || state.items.length === 0) {
      throw new Error("No pending pickup order");
    }
    await setSkipValidation();
    await addItems(state.items);
    await setSplitGroupAttribute(state.splitGroupId);
    await clearSkipValidation();
    const next = Object.assign({}, state, { stage: "pickup-pending" });
    writeLocal(next);
    writeRemote(next);
    window.location.href = "/checkout";
  }

  async function clearPending() {
    clearLocal();
    await clearRemote();
  }

  // ─────────────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────────────
  ns.analyzeCart = analyzeCart;
  ns.getCart = getCart;
  ns.getPending = getPending;
  ns.hasPending = hasPending;
  ns.startSplit = startSplit;
  ns.resumePickup = resumePickup;
  ns.clearPending = clearPending;
  ns.SPLIT_ATTRIBUTE_KEY = SPLIT_ATTRIBUTE_KEY;

  // ─────────────────────────────────────────────────────────────────────
  // ?moonshot_resume_pickup=1 deep-link
  //
  // When a future thank-you-page checkout UI extension is shipped, its
  // resume CTA points at /?moonshot_resume_pickup=1. We detect that on
  // page load here and trigger resumePickup() immediately. Until the
  // extension lands the site-wide banner is the resume affordance; this
  // hook costs nothing and unblocks the extension later.
  // ─────────────────────────────────────────────────────────────────────
  function maybeAutoResume() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("moonshot_resume_pickup") !== "1") return;
    } catch (e) {
      return;
    }
    // Best-effort; if no pending state we silently no-op.
    hasPending().then(function (yes) {
      if (yes) resumePickup().catch(function (err) {
        console.warn("[moonshot-split] auto-resume failed:", err);
      });
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", maybeAutoResume);
  } else {
    maybeAutoResume();
  }
})();
